import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { X, ArrowRight, ArrowLeft, HelpCircle, Info, Lightbulb } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/use-auth";
import { useTheme } from "@/components/ui/theme-provider";
import { useLocation } from "wouter";

// Define tour step type with enhanced properties
type TourStep = {
  target: string;
  title: string;
  content: string;
  placement: 'top' | 'right' | 'bottom' | 'left';
  route?: string; // Optional route path
  icon?: React.ReactNode; // Optional custom icon
  additionalTips?: string[]; // Optional list of additional tips
  actionLabel?: string; // Optional label for an action button
  actionHandler?: () => void; // Optional handler for the action button
};

// Define dynamic path-specific tour steps
const getDynamicPathSteps = (path: string): TourStep[] => {
  if (path === "/") {
    return [
      {
        target: '.dashboard-summary',
        title: 'Dashboard Activity Feed',
        content: 'The activity feed shows all recent actions across your account, including document uploads, audits, and compliance checks.',
        placement: 'bottom',
        additionalTips: [
          'You can filter activities by type, date, or status',
          'Each card links directly to the related resource',
          'The latest updates appear at the top of the feed'
        ],
      },
      {
        target: '.dashboard-stats',
        title: 'Key Performance Metrics',
        content: 'These metrics show your current compliance status, document counts, and audit progress at a glance.',
        placement: 'bottom',
        additionalTips: [
          'Green indicators show compliant items',
          'Yellow indicators show items needing attention',
          'Red indicators show non-compliant items requiring immediate action'
        ],
      },
      {
        target: '.dashboard-charts',
        title: 'Analytics Visualizations',
        content: 'These charts help you track compliance trends over time and across different regulatory categories.',
        placement: 'top',
        additionalTips: [
          'Hover over chart elements to see detailed values',
          'Use the timeframe selector to adjust the data range',
          'Export charts as images for reports by clicking the download icon'
        ],
      }
    ];
  }
  
  if (path.includes("/Organizations")) {
    return [
      {
        target: '.Organizations-list-header',
        title: 'Organizations Management',
        content: 'This section allows you to view and manage all registered aviation Organizations in the system.',
        placement: 'bottom',
        additionalTips: [
          'Organizations are sortable by name, type, or compliance status',
          'Use the search function to quickly find specific Organizations',
          'Click a Organization card to view detailed information'
        ],
      },
      {
        target: '.Organization-actions',
        title: 'Organization Actions',
        content: 'Create new Organizations, edit existing profiles, or manage Organization-specific settings.',
        placement: 'left',
        additionalTips: [
          'Add a new Organization with the "Add Organization" button',
          'Organization logos can be uploaded during creation or editing',
          'Each Organization can have customized AI settings and billing parameters'
        ],
      },
      {
        target: '.Organization-filters',
        title: 'Filtering Options',
        content: 'Filter Organizations by type, location, compliance status, or other properties to find exactly what you need.',
        placement: 'right',
        additionalTips: [
          'Filters can be combined for precise results',
          'Your filter preferences are saved in your session',
          'Clear all filters with the "Reset" button'
        ],
      }
    ];
  }
  
  if (path.includes("/regulations")) {
    return [
      {
        target: '.regulations-header',
        title: 'Regulations Library',
        content: 'Browse, search, and manage aviation regulations from various authorities and jurisdictions.',
        placement: 'bottom',
        additionalTips: [
          'Regulations are organized hierarchically by authority, category, and chapter',
          'Color coding indicates regulation status and applicability',
          'Clicking a regulation opens its detailed view with full text and compliance requirements'
        ],
      },
      {
        target: '.regulation-actions',
        title: 'Regulation Management',
        content: 'Add new regulations, update existing ones, or import regulatory content from external sources.',
        placement: 'left',
        additionalTips: [
          'You can manually enter regulation details or import from PDF documents',
          'Set regulation applicability by Organization type and jurisdiction',
          'Link related regulations to create comprehensive compliance frameworks'
        ],
      },
      {
        target: '.regulation-tree',
        title: 'Regulation Structure',
        content: 'Navigate through the hierarchical structure of regulations, sections, and subsections.',
        placement: 'right',
        additionalTips: [
          'Expand/collapse sections to focus on specific content',
          'Search within the current regulation with the search bar',
          'Highlight key requirements by clicking the star icon'
        ],
      }
    ];
  }
  
  if (path.includes("/audits")) {
    return [
      {
        target: '.audits-list-header',
        title: 'Compliance Audits',
        content: 'View, create, and manage compliance audits that check Organization manuals against applicable regulations.',
        placement: 'bottom',
        additionalTips: [
          'Each audit tracks compliance between specific manuals and regulations',
          'AI-powered analysis highlights potential compliance gaps',
          'Audit results are saved for historical tracking and reporting'
        ],
      },
      {
        target: '.audit-actions',
        title: 'Audit Creation',
        content: 'Create new audits to check compliance between Organization manuals and applicable regulations.',
        placement: 'left',
        additionalTips: [
          'Start a new audit with the "New Audit" button',
          'Select specific manual sections to check against regulations',
          'Choose between comprehensive or focused audit scopes'
        ],
      },
      {
        target: '.audit-results',
        title: 'Compliance Results',
        content: 'Review detailed results showing compliance status, identified gaps, and recommended actions.',
        placement: 'right',
        additionalTips: [
          'Green sections indicate full compliance',
          'Yellow sections may need attention or clarification',
          'Red sections indicate potential non-compliance requiring action',
          'Export results to PDF for distribution or record-keeping'
        ],
      }
    ];
  }
  
  if (path.includes("/billing")) {
    return [
      {
        target: '.billing-summary',
        title: 'Billing Summary',
        content: 'View usage metrics, billing periods, and payment status for your organization.',
        placement: 'bottom',
        additionalTips: [
          'Usage is calculated based on AI tokens consumed during audits',
          'Billing periods typically follow calendar months',
          'Usage caps can be configured to control costs'
        ],
      },
      {
        target: '.usage-metrics',
        title: 'Token Usage Analytics',
        content: 'Monitor AI token consumption across different models and audit activities.',
        placement: 'top',
        additionalTips: [
          'Charts show usage trends over time',
          'Compare usage across different AI models',
          'Identify peak usage periods and optimize accordingly'
        ],
      },
      {
        target: '.billing-records',
        title: 'Billing History',
        content: 'Access past billing records, download invoices, and track payment status.',
        placement: 'left',
        additionalTips: [
          'Download invoices in PDF format for your records',
          'View detailed breakdowns of token usage per billing period',
          'Payment methods and details are managed by your account administrator'
        ],
      }
    ];
  }
  
  // Default steps if no specific path matches
  return [];
};

// Define tour steps for each user role (base steps that apply across the application)
const adminTourSteps: TourStep[] = [
  {
    target: '.sidebar-logo',
    title: 'Welcome to AeroSync',
    content: 'AeroSync streamlines aviation compliance management with intelligent document analysis and audit capabilities.',
    placement: 'right',
    additionalTips: [
      'You\'re logged in as an Administrator with full platform access',
      'You can manage Organizations, regulations, and platform settings',
      'This guided tour will show you the key features and capabilities'
    ],
    icon: <Lightbulb className="h-6 w-6 text-yellow-500" />,
  },
  {
    target: '.sidebar [href="/"]',
    title: 'Dashboard',
    content: 'Your central hub for monitoring compliance status, recent activity, and key metrics across all Organizations.',
    placement: 'right',
    additionalTips: [
      'Get a bird\'s-eye view of compliance across the platform',
      'Track recent activity from all Organizations',
      'Monitor AI token usage and billing information'
    ],
  },
  {
    target: '.sidebar [href="/regulations"]',
    title: 'Regulations Management',
    content: 'Create, organize, and manage aviation regulations from various authorities and jurisdictions.',
    placement: 'right',
    additionalTips: [
      'Add new regulations manually or import from documents',
      'Organize regulations by authority, category, and applicability',
      'Link regulations to specific Organization types and jurisdictions'
    ],
    route: '/regulations',
  },
  {
    target: '.sidebar [href="/regulation-category"]',
    title: 'Regulation Categories',
    content: 'Organize regulations into structured categories for better management and searchability.',
    placement: 'right',
    additionalTips: [
      'Create hierarchical category structures',
      'Assign color codes for visual identification',
      'Map categories to specific operational domains'
    ],
    route: '/regulation-category',
  },
  {
    target: '.sidebar [href="/authorities"]',
    title: 'Authorities Management',
    content: 'Manage aviation regulatory authorities and their configurations for document processing.',
    placement: 'right',
    additionalTips: [
      'Add new regulatory authorities with contact information',
      'Configure document patterns for automatic recognition',
      'Manage authority status and processing settings'
    ],
    route: '/authorities',
  },
  {
    target: '.sidebar [href="/Organizations"]',
    title: 'Organizations Management',
    content: 'Add, edit, and manage aviation Organizations of various types, from airlines to ground services.',
    placement: 'right',
    additionalTips: [
      'Create comprehensive Organization profiles with logos',
      'Manage team members and access permissions',
      'Configure Organization-specific AI settings and billing parameters'
    ],
    route: '/Organizations',
  },
  {
    target: '.sidebar [href="/compliance"]',
    title: 'Compliance Dashboard',
    content: 'Track compliance status across all Organizations with visual indicators and detailed breakdowns.',
    placement: 'right',
    additionalTips: [
      'View compliance by Organization, regulation, or category',
      'Identify areas requiring immediate attention',
      'Generate compliance reports for regulatory submissions'
    ],
    route: '/compliance',
  },
  {
    target: '.sidebar [href="/billing"]',
    title: 'Billing Management',
    content: 'Manage billing information, token usage caps, and generate invoices for all registered Organizations.',
    placement: 'right',
    additionalTips: [
      'Set token usage caps to control costs',
      'Generate and download monthly invoices',
      'Track payment status and history'
    ],
    route: '/billing',
  },
  {
    target: '.sidebar [href="/admin-users"]',
    title: 'Admin User Management',
    content: 'Add and manage administrator colleagues who can access and manage the AeroSync platform.',
    placement: 'right',
    additionalTips: [
      'Create new admin accounts with specific permissions',
      'Activate or deactivate admin access as needed',
      'Manage role-based permissions for platform features'
    ],
    route: '/admin-users',
  },
  {
    target: '.theme-toggle',
    title: 'Appearance Settings',
    content: 'Customize your viewing experience by switching between light and dark themes.',
    placement: 'top',
    additionalTips: [
      'Light theme is optimized for daytime use',
      'Dark theme reduces eye strain in low-light environments',
      'Your preference is saved across sessions'
    ],
  },
  {
    target: '.user-menu',
    title: 'User Settings',
    content: 'Access your profile settings, notification preferences, and account management options.',
    placement: 'bottom',
    additionalTips: [
      'Update your personal information',
      'Configure notification preferences',
      'Manage security settings and password'
    ],
  },
];

const auditorTourSteps: TourStep[] = [
  {
    target: '.sidebar-logo',
    title: 'Welcome to AeroSync',
    content: 'AeroSync helps you manage your Organization\'s compliance with aviation regulations through intelligent document analysis.',
    placement: 'right',
    additionalTips: [
      'You\'re logged in as an Auditor for your Organization',
      'You can manage manuals, run compliance audits, and view Organization information',
      'This guided tour will show you the key features available to you'
    ],
    icon: <Lightbulb className="h-6 w-6 text-yellow-500" />,
  },
  {
    target: '.sidebar [href="/"]',
    title: 'Dashboard',
    content: 'Your central hub showing your Organization\'s compliance status, recent activity, and important notifications.',
    placement: 'right',
    additionalTips: [
      'View your compliance status at a glance',
      'Track recent audit and document activities',
      'See reminders for upcoming regulatory deadlines'
    ],
  },
  {
    target: '.sidebar [href="/manuals"]',
    title: 'Manuals Management',
    content: 'Upload, organize, and manage your Organization\'s operational manuals and procedures.',
    placement: 'right',
    additionalTips: [
      'Upload manuals in PDF format for automatic parsing',
      'Organize documents by type, department, or function',
      'Track document versions and revision history'
    ],
    route: '/manuals',
  },
  {
    target: '.sidebar [href="/audits"]',
    title: 'Compliance Audits',
    content: 'Run AI-powered audits to check your manuals against applicable regulations.',
    placement: 'right',
    additionalTips: [
      'Create new audits with focused or comprehensive scope',
      'Review detailed compliance analysis with highlighted gaps',
      'Generate audit reports for internal or regulatory use'
    ],
    route: '/audits',
  },
  {
    target: '.sidebar [href="/compliance"]',
    title: 'Compliance Status',
    content: 'View your Organization\'s current compliance status across all applicable regulations.',
    placement: 'right',
    additionalTips: [
      'Filter compliance view by regulatory domain',
      'Track progress on addressing compliance gaps',
      'Set priorities for non-compliant items'
    ],
    route: '/compliance',
  },
  {
    target: '.sidebar [href="/billing"]',
    title: 'Billing Information',
    content: 'Review your Organization\'s AI token usage, billing records, and usage limits.',
    placement: 'right',
    additionalTips: [
      'Monitor token consumption to stay within budget',
      'View billing history and download invoices',
      'Track usage trends to optimize audit efficiency'
    ],
    route: '/billing',
  },
  {
    target: '.sidebar [href="/profile"]',
    title: 'Organization Profile',
    content: 'View and manage your Organization\'s profile information, technical details, and team members.',
    placement: 'right',
    additionalTips: [
      'Update Organization information as needed',
      'Manage team member access and permissions',
      'Configure Organization-specific settings and preferences'
    ],
    route: '/profile',
  },
  {
    target: '.theme-toggle',
    title: 'Appearance Settings',
    content: 'Customize your viewing experience by switching between light and dark themes.',
    placement: 'top',
    additionalTips: [
      'Light theme is optimized for daytime use',
      'Dark theme reduces eye strain in low-light environments',
      'Your preference is saved across sessions'
    ],
  },
  {
    target: '.user-menu',
    title: 'User Settings',
    content: 'Access your profile settings, notification preferences, and account options.',
    placement: 'bottom',
    additionalTips: [
      'Update your personal information',
      'Configure notification preferences',
      'Manage your user password and security settings'
    ],
  },
];

interface TourStepProps {
  step: TourStep;
  currentStepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
  onAction?: () => void;
}

// Enhanced tooltip component for each tour step
function TourStep({ step, currentStepIndex, totalSteps, onNext, onPrev, onClose, onAction }: TourStepProps) {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [activeTip, setActiveTip] = useState(0);
  const { theme } = useTheme();
  
  useEffect(() => {
    const targetElement = document.querySelector(step.target);
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      const { placement } = step;
      
      let x = 0;
      let y = 0;
      
      // Larger tooltip dimensions
      const tooltipWidth = 420;
      const tooltipHeight = 300;
      
      switch (placement) {
        case 'top':
          x = rect.left + rect.width / 2 - tooltipWidth / 2;
          y = rect.top - tooltipHeight - 20;
          break;
        case 'right':
          x = rect.right + 20;
          y = rect.top + rect.height / 2 - tooltipHeight / 2;
          break;
        case 'bottom':
          x = rect.left + rect.width / 2 - tooltipWidth / 2;
          y = rect.bottom + 20;
          break;
        case 'left':
          x = rect.left - tooltipWidth - 20;
          y = rect.top + rect.height / 2 - tooltipHeight / 2;
          break;
      }

      // Ensure the tooltip stays within viewport
      x = Math.max(20, Math.min(window.innerWidth - tooltipWidth - 20, x));
      y = Math.max(20, Math.min(window.innerHeight - tooltipHeight - 20, y));
      
      setPosition({ x, y });
      
      // Add highlight to target element
      targetElement.classList.add('tour-highlight');
      
      // Add a pulse animation for better visibility
      targetElement.classList.add('tour-pulse');
      
      // Scroll element into view if needed
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      return () => {
        // Remove highlight when unmounting
        targetElement.classList.remove('tour-highlight');
        targetElement.classList.remove('tour-pulse');
      };
    }
  }, [step]);

  // Rotate through additional tips every few seconds
  useEffect(() => {
    if (step.additionalTips && step.additionalTips.length > 1) {
      const interval = setInterval(() => {
        setActiveTip(prev => (prev + 1) % step.additionalTips!.length);
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [step.additionalTips]);
  
  return (
    <motion.div 
      className="fixed z-50 w-[420px] rounded-lg shadow-xl bg-white dark:bg-gray-700 border border-blue-200 dark:border-blue-300 tour-tooltip"
      style={{ 
        left: position.x, 
        top: position.y,
        backgroundColor: theme?.includes('dark') ? '#2d3748' : '#ffffff', 
        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 114, 171, 0.3)"
      }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.3 }}
    >
      <div className="p-6 bg-white dark:bg-gray-700">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center">
            {step.icon || <Info className="h-6 w-6 text-blue-500 mr-3" />}
            <h3 className="font-bold text-xl text-primary">{step.title}</h3>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose}
            className="h-8 w-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        <div className="mb-4 text-sm text-gray-700 dark:text-gray-100 leading-relaxed">
          {step.content}
        </div>
        
        {step.additionalTips && step.additionalTips.length > 0 && (
          <div className="mb-5 bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 border-l-4 border-blue-500">
            <motion.div
              key={activeTip}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex items-start"
            >
              <Lightbulb className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-700 dark:text-blue-200">
                {step.additionalTips[activeTip]}
              </p>
            </motion.div>
            
            {step.additionalTips.length > 1 && (
              <div className="flex justify-center mt-2 space-x-1">
                {step.additionalTips.map((_, index) => (
                  <div 
                    key={index} 
                    className={`h-1.5 w-1.5 rounded-full ${index === activeTip ? 'bg-blue-500' : 'bg-blue-200 dark:bg-blue-700'}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        
        {step.actionLabel && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onAction || onNext}
            className="mb-4 w-full border-blue-200 hover:bg-blue-50 dark:border-blue-800 dark:hover:bg-blue-900/50"
          >
            {step.actionLabel}
          </Button>
        )}
        
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500 flex items-center">
            <div className="flex space-x-1 mr-2">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div 
                  key={i} 
                  className={`h-1.5 w-1.5 rounded-full ${i === currentStepIndex ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-700'}`}
                />
              ))}
            </div>
            <span>{currentStepIndex + 1} of {totalSteps}</span>
          </div>
          
          <div className="flex gap-2">
            {currentStepIndex > 0 && (
              <Button size="sm" variant="outline" onClick={onPrev}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
            )}
            {currentStepIndex < totalSteps - 1 ? (
              <Button size="sm" onClick={onNext} className="bg-primary hover:bg-primary/90">
                Next
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button size="sm" onClick={onClose} className="bg-primary hover:bg-primary/90">
                Finish
              </Button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function GuidedTour() {
  const { user } = useAuth();
  const [location] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  
  // State to track user completion of the tour
  const [completedTour, setCompletedTour] = useState<Record<string, boolean>>(() => {
    if (typeof window === 'undefined' || !user?.role) return {};
    try {
      const saved = localStorage.getItem(`aerosync-tour-completed-${user.role}`);
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });
  
  // Get base steps for the user role
  const baseSteps = user?.role === 'admin' ? adminTourSteps : auditorTourSteps;
  
  // Get any path-specific steps for the current route
  const pathSpecificSteps = getDynamicPathSteps(location);
  
  // Get the appropriate tour steps - combine base steps with any page-specific steps
  const getTourSteps = useCallback(() => {
    // On the root path or general navigation, use the full base tour
    if (location === '/' || !pathSpecificSteps.length) {
      return baseSteps;
    }
    
    // On specific pages, focus on the page-specific steps
    return pathSpecificSteps;
  }, [location, baseSteps, pathSpecificSteps]);
  
  const tourSteps = getTourSteps();
  
  // Check if navigation requires route change
  const handleStepNavigation = useCallback((nextIndex: number) => {
    const currentStep = tourSteps[currentStepIndex];
    const nextStep = tourSteps[nextIndex];
    
    // If the next step has a specific route and it's different from current location
    if (nextStep?.route && nextStep.route !== location) {
      // We need to navigate to that route first
      window.location.href = nextStep.route;
      return true; // Navigation is happening
    }
    
    return false; // No navigation needed
  }, [tourSteps, currentStepIndex, location]);
  
  // No auto-start tour - user will start it manually from the button
  // We're keeping this effect just to detect page changes and prepare the appropriate tour content
  useEffect(() => {
    if (!user) return;
    
    // This effect will just prepare the tour data based on current path
    // But won't auto-start the tour
    
    // Reset step index when location changes to ensure tour starts from beginning when manually activated
    if (pathSpecificSteps.length > 0) {
      setCurrentStepIndex(0);
    }
  }, [user, location, pathSpecificSteps]);
  
  // Action handler for any custom actions on steps
  const handleStepAction = useCallback(() => {
    const currentStep = tourSteps[currentStepIndex];
    if (currentStep?.actionHandler) {
      currentStep.actionHandler();
    } else {
      handleNext();
    }
  }, [tourSteps, currentStepIndex]);
  
  const handleNext = () => {
    if (currentStepIndex < tourSteps.length - 1) {
      // Check if navigation is needed
      const navigationNeeded = handleStepNavigation(currentStepIndex + 1);
      
      if (!navigationNeeded) {
        setCurrentStepIndex((prev) => prev + 1);
      }
    } else {
      handleClose();
    }
  };
  
  const handlePrev = () => {
    if (currentStepIndex > 0) {
      // Check if navigation is needed
      const navigationNeeded = handleStepNavigation(currentStepIndex - 1);
      
      if (!navigationNeeded) {
        setCurrentStepIndex((prev) => prev - 1);
      }
    }
  };
  
  const handleClose = () => {
    setIsOpen(false);
    setCurrentStepIndex(0);
    
    if (user?.role) {
      // Mark this specific path as completed
      const newCompletedTour = { ...completedTour };
      
      if (pathSpecificSteps.length > 0) {
        newCompletedTour[location] = true;
      } else {
        newCompletedTour['base'] = true;
      }
      
      localStorage.setItem(`aerosync-tour-completed-${user.role}`, JSON.stringify(newCompletedTour));
      setCompletedTour(newCompletedTour);
    }
  };
  
  const handleOpen = () => {
    setIsOpen(true);
    setCurrentStepIndex(0);
  };
  
  const handleRestartTour = () => {
    // Clear all tour completion flags
    if (user?.role) {
      localStorage.removeItem(`aerosync-tour-completed-${user.role}`);
      setCompletedTour({});
    }
    handleOpen();
  };
  
  if (!user) return null;
  
  return (
    <>
      {/* Tour tooltip */}
      <AnimatePresence>
        {isOpen && tourSteps[currentStepIndex] && (
          <TourStep
            step={tourSteps[currentStepIndex]}
            currentStepIndex={currentStepIndex}
            totalSteps={tourSteps.length}
            onNext={handleNext}
            onPrev={handlePrev}
            onClose={handleClose}
            onAction={handleStepAction}
          />
        )}
      </AnimatePresence>
      
      {/* Tour buttons */}
      <div className="relative group">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleOpen}
          className="h-9 w-9 rounded-full hover:bg-blue-50 dark:hover:bg-blue-900/30 text-gray-700 dark:text-gray-200"
          title="Start guided tour"
        >
          <HelpCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </Button>
        
        {/* Restart tour button (only shows when hovering over help button) */}
        <div className="absolute hidden right-0 bottom-full mb-2 group-hover:block z-50">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRestartTour}
            className="text-xs py-1 h-auto shadow-md"
          >
            Restart Tours
          </Button>
        </div>
      </div>
      
      {/* Global styles for highlighted elements */}
      {isOpen && (
        <style>
          {`
            .tour-highlight {
              position: relative;
              z-index: 49;
              box-shadow: 0 0 0 4px rgba(0, 114, 171, 0.5);
              border-radius: 4px;
              animation: pulse-border 2s infinite;
            }
            
            .tour-pulse {
              animation: pulse-border 2s infinite;
            }
            
            @keyframes pulse-border {
              0% {
                box-shadow: 0 0 0 4px rgba(0, 114, 171, 0.5);
              }
              70% {
                box-shadow: 0 0 0 8px rgba(0, 114, 171, 0.2);
              }
              100% {
                box-shadow: 0 0 0 4px rgba(0, 114, 171, 0.5);
              }
            }
            
            /* Dim the rest of the UI to focus on the highlighted element */
            body::after {
              content: '';
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(0, 0, 0, 0.4);
              z-index: 48;
              pointer-events: none;
              /* Removed blur effect as it was making the entire page blurry */
            }
            
            /* Make the tour tooltip stand out more */
            .tour-tooltip {
              box-shadow: 0 0 0 3px rgba(0, 114, 171, 0.5), 
                         0 10px 30px rgba(0, 0, 0, 0.25), 
                         0 0 25px rgba(255, 255, 255, 0.3) !important;
              z-index: 50 !important;
              animation: tooltip-glow 3s ease-in-out infinite;
              border: 1px solid rgba(255, 255, 255, 0.6) !important;
            }
            
            @keyframes tooltip-glow {
              0% {
                box-shadow: 0 0 0 3px rgba(0, 114, 171, 0.5), 
                           0 10px 30px rgba(0, 0, 0, 0.25), 
                           0 0 25px rgba(255, 255, 255, 0.3);
              }
              50% {
                box-shadow: 0 0 0 3px rgba(0, 114, 171, 0.7), 
                           0 10px 30px rgba(0, 0, 0, 0.3), 
                           0 0 35px rgba(255, 255, 255, 0.5);
              }
              100% {
                box-shadow: 0 0 0 3px rgba(0, 114, 171, 0.5), 
                           0 10px 30px rgba(0, 0, 0, 0.25), 
                           0 0 25px rgba(255, 255, 255, 0.3);
              }
            }
          `}
        </style>
      )}
    </>
  );
}
